import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, forkJoin, map, of, switchMap, takeLast, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

import { FieldSet, FieldType, GenerateReportResponse, GeneratedReportsRequestQuery, ReportDetailsModel, ReportFilters, ReportItem, ReportPage, S3LinkResponse, SessionAuthorization, Show } from '@models/index';
import _reportTypes from '@utility/constants/reports.constant.json';
import { Breadcrumb } from '@models/index';

import { ApiService, AuthService } from './index';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  public title$ = new BehaviorSubject('Select Report Type');
  public fieldSets$ = new BehaviorSubject<FieldSet[]>([]);
  public generatedReports$ = new BehaviorSubject<ReportDetailsModel[]>([]);
  public show = Show.REPORT_TYPES;
  public show$ = new BehaviorSubject(this.show);
  public isLoading$ = new BehaviorSubject(false);
  public reportsTypes: ReportItem[] = [];
  public reportTypes$ = new BehaviorSubject<ReportItem[]>([])
  public reportType: ReportItem | null = null;
  public generatedReports: ReportDetailsModel[] = [];
  public reportsBatchToShow = 50;
  public reportDescriptionText = ''
  public breadcrumbs$ = new BehaviorSubject<string[]>([]);
  public activeBreadcrumb$ = new BehaviorSubject('');
  public reportPage$ = combineLatest([
    this.show$,
    this.title$,
    this.fieldSets$,
    this.reportTypes$
  ]).pipe(
    map(([
      show,
      title,
      fieldSets,
      reportTypes,
    ]): ReportPage => ({
      show,
      title,
      fieldSets,
      reportTypes
    }))
  )

  constructor(
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
  ) { }

  public updateReportPage({ title, show }): void {
    this.show = Show[show]
    this.show$.next(Show[show]);
    this.title$.next(title);
    this.fieldSets$.next([]);
  }

public viewReportParams(reportType: ReportItem, extraParams?: Record<string, string>): void {
  this.reportType = { ...reportType };
  this.updateReportPage({ title: reportType.title, show: 'FORM' });
  this.updateBreadcrumbs(reportType.title, false);

  if (reportType.fieldSets) {
    this.fieldSets$.next(reportType.fieldSets);
  } else {
    forkJoin([
      this.apiService.getReportParams(reportType.type),
      this.apiService.getInputFieldOptionsForReport('report_type', reportType.type, extraParams),
    ])
      .pipe(
        map(([fieldSets, inputFieldOptionsResponse]) => {
          return fieldSets.map(fieldSet => {
            const fields = fieldSet.fields ?? [];
            fieldSet.fields = fields.map(field => {
              const selects = inputFieldOptionsResponse.Data;
              if (field.type === FieldType.SELECT && field.name in selects) {
                const baseOptions = selects[field.name].map(option => {
                  if (Object.prototype.hasOwnProperty.call(option, 1)) {
                    return ({ id: option[1], value: `${option[0]} (${option[1]})` });
                  } else {
                    return ({ id: option[0], value: `${option[0]}` });
                  }
                });
                const noneSelected = { id: -1, value: `Select ${field.label}` };
                field.options = !field.multiSelect ? [noneSelected, ...baseOptions] : baseOptions;
              }
              return field;
            });
            return fieldSet;
          });
        }),
        takeLast(1),
      )
      .subscribe({
        next: fieldSets => {
          reportType.fieldSets = fieldSets;
          this.fieldSets$.next(fieldSets ?? []);
        },
        error: error => {
          console.error(error);
          // TODO: replace with a better dialog modal
          window.alert('Unable to fetch data for the form field(s) properly.');
        }
      });
  }
}

  public generateReport(report_filters: ReportFilters): Observable<GenerateReportResponse | null> {
    const body = {
      report_filters,
      description: this.reportDescriptionText,
      report_type: this.reportType?.type ?? '',
      report_requester: this.authService.userId,
    };
    this.updateBreadcrumbs(Breadcrumb.HISTORY, false);

    return this.apiService.generateReport(body);
  }

  public getGeneratedReports(params: string, query: GeneratedReportsRequestQuery) {

    return this.apiService.getGeneratedReports(params, query);
  }

  public getPendingReports(): Observable<unknown> {

    return this.apiService.getPendingReports();
  }

  public updateBreadcrumbs(breadcrumbTitle: string, userClickedBreadcrumbLink: boolean): void {
    this.activeBreadcrumb$.next(breadcrumbTitle)
    if (userClickedBreadcrumbLink) {
      if (breadcrumbTitle === Breadcrumb.REPORT_LIST) {
        this.updateReportPage({ title: 'Select Report Type', show: 'REPORT_TYPES' })
        this.generatedReports = [];
        this.generatedReports$.next([]);
        this.breadcrumbs$.next([]);
        return;
      }
      if (breadcrumbTitle === Breadcrumb.HISTORY) {
        this.updateReportPage({ title: 'Reports Pickup', show: 'GENERATED_REPORTS' })
        this.generatedReports = [];
        this.generatedReports$.next([]);
        this.reportsTypes.forEach(reportType => {
          reportType.subReports.forEach(subReportType => {
            if (subReportType.title === this.breadcrumbs$[1]) {
              this.reportType = subReportType
            }
          })
        })
        return;
      }
      if (this.reportType) {
        this.viewReportParams(this.reportType);
        return;
      }
      return;
    }
    this.breadcrumbs$.next([Breadcrumb.REPORT_LIST, this.reportType?.title || '', Breadcrumb.HISTORY]);
  }

  public setSessionAuth(): Observable<SessionAuthorization[]> {
    return this.activatedRoute.queryParams
      .pipe(
        switchMap(params => {
          if (!params || !params['session_uuid']) {

            return of([]);
          }
          this.authService.sessionUUID = params['session_uuid'];

          return this.authService.fetchAndStoreToken(params['session_uuid']);
        })
      )
  }

  public updateUserAccessibleReports(accessObjectUniqueName: string[], isBetaUser: boolean, features: string[]): void {
    this.reportsTypes = _reportTypes
      .filter((report) => accessObjectUniqueName.includes(report.type))
      .sort((a, b) => {
        if (a.type === 'crystal_qa_utility') return 1;
        if (b.type === 'crystal_qa_utility') return -1;
        return a.title.localeCompare(b.title)
      });

    //For beta users, show all reports. For non beta users, show reports available in features
    if (!isBetaUser) {
      this.reportsTypes = this.reportsTypes.reduce((reportList: ReportItem[], report) => {
        const availableSubReports = report.subReports.filter(subReport =>
          features.includes(subReport.type.toLocaleUpperCase())
        );
        if (availableSubReports.length > 0) {
          reportList.push({ ...report, subReports: availableSubReports });
        }
        return reportList;
      }, []);
    }
    this.reportTypes$.next(this.reportsTypes);
  }

  public downloadReport(report: ReportDetailsModel, s3Location: string): Observable<S3LinkResponse> {
    return this.apiService.getS3FileLink(report.reportName, report.requester, s3Location).pipe(
      tap(res => {
        if (res.downloadUrl) {
          const downloadLink = document.createElement('a');
          downloadLink.href = res.downloadUrl;
          downloadLink.click();
        }
      }),
    )
  }

  // Generate all reports for Reporting Utility
  public generateAllReports(report_filters): Observable<GenerateReportResponse | null> {
    const { report_type, report_params, report_format } = report_filters
    const body = {
      report:"ALL",
      report_type,
      report_params,
      report_format,
      report_requester: this.authService.userId,
    };

    return this.apiService.generateAllReports(body);
  }

  // View params for Reporting Utility
  public viewAllReportParams(reportType: ReportItem): void {
    this.reportType = { ...reportType };
    this.updateReportPage({ title: reportType.title, show: 'FORM' });
    this.breadcrumbs$.next([]);

    if (reportType.fieldSets?.length) {
      this.fieldSets$.next(reportType.fieldSets);
      return;
    }

    this.apiService.getReportParams(reportType.type).subscribe({
      next: fieldSets => {
        reportType.fieldSets = fieldSets ?? [];
        this.fieldSets$.next(reportType.fieldSets);
      },
      error: err => {
        console.error(err);
        // TODO: replace with a better dialog modal
        window.alert('Unable to fetch data for the form field(s) properly.');
      }
    });
  }
}

